import { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { tours } from '../data/data';
import './Modals.css';

// Sanitize URLs used in CSS background-image to prevent CSS injection
const safeBgUrl = (url) => {
  try {
    const u = new URL(url);
    // Only allow https images from trusted origins
    if (u.protocol !== 'https:') return '';
    return `url("${u.href.replace(/"/g, '%22')}")`;
  } catch {
    return '';
  }
};

/* ─── INCLUSION ICON MAPPER ─── */
function InclusionIcon({ label }) {
  const l = label.toLowerCase();

  // Hotel / Accommodation
  if (/hotel|resort|villa|bungalow|lodge|stay|heritage|overwater|beach resort|ryokan|eco lodge/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21V9l9-6 9 6v12H3z" /><path d="M9 21V13h6v8" />
      </svg>
    );
  }
  // Meals / Food
  if (/breakfast|lunch|dinner|meal|food|dining|all-inclusive|all inclusive|mapi|cpi|api/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8h1a4 4 0 0 1 0 8h-1" /><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" /><line x1="6" y1="1" x2="6" y2="4" /><line x1="10" y1="1" x2="10" y2="4" /><line x1="14" y1="1" x2="14" y2="4" />
      </svg>
    );
  }
  // Flights / Air
  if (/flight|air|fly|seaplane|domestic flight|airplane/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19.5 2.5S18 2 16.5 3.5L13 7 4.8 5.2 3.3 6.7l4 4-2 2-4-1-1.5 1.5 3.5 2.5 2.5 3.5 1.5-1.5-1-4 2-2 4 4 1.5-1.5z" />
      </svg>
    );
  }
  // Car / Cab / Transfers / Transport
  if (/cab|car|transfer|transport|driver|pickup|drop|taxi|private car/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="3" width="15" height="13" rx="2" /><path d="M16 8h5l2 5v4h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
      </svg>
    );
  }
  // Boat / Ferry / Cruise / Houseboat
  if (/boat|ferry|cruise|houseboat|yacht|ship|sail|water sport|sea|lagoon exploration/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 20a2.4 2.4 0 0 0 2 1 2.4 2.4 0 0 0 2-1 2.4 2.4 0 0 1 2-1 2.4 2.4 0 0 1 2 1 2.4 2.4 0 0 0 2 1 2.4 2.4 0 0 0 2-1 2.4 2.4 0 0 1 2-1 2.4 2.4 0 0 1 2 1" />
        <path d="M4 17l-1-4h18l-2 4" /><path d="M12 2v9" /><path d="M8 9l4-4 4 4" />
      </svg>
    );
  }
  // Guide / Expert
  if (/guide|expert|local guide|tour guide|escort/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" /><path d="M12 14c-5 0-8 2.5-8 4v1h16v-1c0-1.5-3-4-8-4z" />
        <path d="M17 3l1.5 1.5L16 7" />
      </svg>
    );
  }
  // Sightseeing / Tour / Attractions / Tickets
  if (/sightseeing|tour|attraction|ticket|visit|experience|cultural|activities|safari|excursion/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" /><circle cx="12" cy="9" r="2.5" />
      </svg>
    );
  }
  // Train / Rail Pass
  if (/train|rail|jr pass|pass/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="3" width="16" height="13" rx="3" /><path d="M4 16l-2 4h20l-2-4" /><path d="M9 19h6" /><line x1="8" y1="8" x2="16" y2="8" /><circle cx="9" cy="12" r="1" /><circle cx="15" cy="12" r="1" />
      </svg>
    );
  }
  // Water Sports / Scuba / Snorkeling
  if (/water sport|scuba|snorkel|diving|swim/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12c1.5-2 3.5-2 5 0s3.5 2 5 0 3.5-2 5 0" /><path d="M2 17c1.5-2 3.5-2 5 0s3.5 2 5 0 3.5-2 5 0" /><path d="M12 2v6" /><circle cx="12" cy="5" r="2" />
      </svg>
    );
  }
  // Camel / Safari rides
  if (/camel|safari|jeep ride|ride/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="12" rx="9" ry="6" /><path d="M3 12c0-3 4-6 9-6s9 3 9 6" /><path d="M8 18v2m8-2v2" />
      </svg>
    );
  }
  // Park fees / Entry
  if (/park fee|entry|permit|fee/.test(l)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M16 12a4 4 0 0 1-8 0" />
      </svg>
    );
  }
  // Default: checkmark
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

/* ─── TOUR DETAIL MODAL ─── */
export function TourModal({ tour, onClose, onBook }) {
  if (!tour) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ backgroundImage: safeBgUrl(tour.img) }}>
          <div className="modal-header-overlay" />
          <button className="modal-close" onClick={onClose}>×</button>
          <div className="modal-header-content">
            <div className="modal__eyebrow">{tour.region}</div>
            <div className="modal__title">{tour.name}</div>
          </div>
        </div>

        <div className="modal-body">
          <div className="modal__meta-row">
            <div className="modal__meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>
              {tour.days ? `${tour.days} Days` : tour.customizableOptions?.duration || 'Flexible'}
            </div>
            <div className="modal__meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4" /><path d="M20 21a8 8 0 1 0-16 0" /></svg>
              {tour.type}
            </div>
          </div>

          <p className="modal__desc">{tour.description}</p>

          {tour.destinations && (
            <>
              <div className="modal__section-title">Destinations Covered</div>
              <div className="modal__highlights" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
                {tour.destinations.map((d, i) => (
                  <div key={i} className="modal__highlight-item" style={{ background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)', justifyContent: 'center', textAlign: 'center' }}>
                    📍 {d}
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="modal__section-title">Tour Highlights</div>
          <div className="modal__highlights">
            {tour.highlights.map((h, i) => (
              <div key={i} className="modal__highlight-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                {h}
              </div>
            ))}
          </div>

          {tour.customizableOptions && (
            <>
              <div className="modal__section-title">Customizable Options</div>
              <div className="modal__highlights" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {tour.customizableOptions.duration && (
                  <div className="modal__highlight-item" style={{ display: 'block', background: 'transparent' }}>
                    <strong style={{ color: 'var(--text)' }}>Duration:</strong> {tour.customizableOptions.duration}
                  </div>
                )}
                {tour.customizableOptions.budget && (
                  <div className="modal__highlight-item" style={{ display: 'block', background: 'transparent' }}>
                    <strong style={{ color: 'var(--text)' }}>Budget:</strong> {tour.customizableOptions.budget}
                  </div>
                )}
                {(tour.customizableOptions.stay || tour.customizableOptions.hotelCategory) && (
                  <div className="modal__highlight-item" style={{ display: 'block', background: 'transparent' }}>
                    <strong style={{ color: 'var(--text)' }}>Hotels:</strong> {(tour.customizableOptions.stay || tour.customizableOptions.hotelCategory || []).join(', ')}
                  </div>
                )}
                {tour.customizableOptions.transport && (
                  <div className="modal__highlight-item" style={{ display: 'block', background: 'transparent' }}>
                    <strong style={{ color: 'var(--text)' }}>Transport:</strong> {(tour.customizableOptions.transport || []).join(', ')}
                  </div>
                )}
                {tour.customizableOptions.activities && (
                  <div className="modal__highlight-item" style={{ display: 'block', background: 'transparent' }}>
                    <strong style={{ color: 'var(--text)' }}>Activities:</strong> {(tour.customizableOptions.activities || []).join(', ')}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="modal__section-title">What's Included</div>
          <div className="modal__inclusions-grid">
            {tour.includes.map((inc, i) => (
              <div key={i} className="modal__inclusion-pill">
                <span className="modal__inclusion-icon">
                  <InclusionIcon label={inc} />
                </span>
                <span className="modal__inclusion-label">{inc}</span>
              </div>
            ))}
          </div>

          <div className="modal__actions">
            <button className="btn-primary" style={{ flex: 1 }} onClick={() => onBook(tour)}>
              Book This Tour
            </button>
            <button className="btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── BOOKING / CONSULTATION MODAL ─── */
export function BookingModal({ tour, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    city: '',
    destination: '',
    travelDates: '',
    days: '',
    hotelCategory: '3 Star',
    mealPlan: 'CPI (Breakfast only)'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [childAges, setChildAges] = useState([]);
  const [rooms, setRooms] = useState(1);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowGuestDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChildrenChange = (delta) => {
    const newCount = children + delta;
    if (newCount >= 0) {
      setChildren(newCount);
      if (delta > 0) {
        setChildAges([...childAges, '']);
      } else {
        setChildAges(childAges.slice(0, -1));
      }
      if (errors.guests) setErrors(prev => ({ ...prev, guests: null }));
    }
  };

  const handleAgeChange = (index, value) => {
    const newAges = [...childAges];
    newAges[index] = value;
    setChildAges(newAges);
    if (errors.guests) setErrors(prev => ({ ...prev, guests: null }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      newErrors.fullName = "Full Name must be at least 2 characters.";
    }
    const phoneRegex = /^[+]?[0-9\s\-]{7,15}$/;
    if (!formData.phone || !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Enter a valid phone number (7-15 digits).";
    }
    if (!formData.city.trim()) {
      newErrors.city = "City is required.";
    }
    if (!formData.destination.trim()) {
      newErrors.destination = "Travel destination is required.";
    }
    if (!formData.travelDates.trim()) {
      newErrors.travelDates = "Preferred travel dates are required.";
    }
    if (!formData.days || parseInt(formData.days, 10) < 1) {
      newErrors.days = "Please enter a valid number of days.";
    }
    if (children > 0 && childAges.some(age => age === '')) {
      newErrors.guests = "Please specify the ages of all children.";
      setShowGuestDropdown(true);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);

      const tourName = tour ? tour.name : "Custom Consultation";
      const agesText = children > 0 ? childAges.join(', ') : "None";

      const templateParams = {
        to_email: "nextdreamholidays07@gmail.com",
        tour_name: tourName,
        customer_name: formData.fullName,
        customer_phone: formData.phone,
        customer_city: formData.city,
        destination: formData.destination,
        travel_dates: formData.travelDates,
        duration: formData.days,
        adults: adults,
        children: children,
        child_ages: agesText,
        rooms: rooms,
        hotel_category: formData.hotelCategory,
        meal_plan: formData.mealPlan,
      };

      try {
        // REPLACE THESE WITH YOUR ACTUAL EMAILJS KEYS FROM THE DASHBOARD
        await emailjs.send(
          'next_dream_holidays',
          'template_7tuvn4q',
          templateParams,
          'Eg4BMqCi51HEahSGv'
        );

        onSuccess(tour ? `Booking request for "${tour.name}" sent!` : 'Consultation request sent! We will call you within 24 hours.');
        onClose();
      } catch (error) {
        console.error('EmailJS Error:', error);
        alert('Failed to send your request. Please try again later.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const ErrorMsg = ({ msg }) => msg ? <div style={{ color: '#e53e3e', fontSize: 15, marginTop: 4 }}>{msg}</div> : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-box--form" onClick={e => e.stopPropagation()}>
        <div className="modal-form-header">
          <div>
            <div className="modal__eyebrow" style={{ color: 'var(--accent)', marginBottom: 4 }}>
              {tour ? 'Book Tour' : 'Free Consultation'}
            </div>
            <h3 className="modal-form-title">{tour ? tour.name : 'Plan Your Dream Trip'}</h3>
            {tour && <div className="modal-form-price">{tour.days ? `${tour.days} days` : 'Flexible Duration'}</div>}
          </div>
          <button className="modal-close modal-close--dark" onClick={onClose}>×</button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-input" type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="Your full name" style={{ borderColor: errors.fullName ? '#e53e3e' : '' }} />
              <ErrorMsg msg={errors.fullName} />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number *</label>
              <input className="form-input" type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+91 98765 43210" style={{ borderColor: errors.phone ? '#e53e3e' : '' }} />
              <ErrorMsg msg={errors.phone} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">City *</label>
              <input className="form-input" type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="Your current city" style={{ borderColor: errors.city ? '#e53e3e' : '' }} />
              <ErrorMsg msg={errors.city} />
            </div>
            <div className="form-group">
              <label className="form-label">Travel Destination *</label>
              <input className="form-input" type="text" name="destination" value={formData.destination} onChange={handleInputChange} placeholder="Where do you want to go?" style={{ borderColor: errors.destination ? '#e53e3e' : '' }} />
              <ErrorMsg msg={errors.destination} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Preferred Travel Dates *</label>
              <input className="form-input" type="text" name="travelDates" value={formData.travelDates} onChange={handleInputChange} placeholder="e.g. 01/01/2026" style={{ borderColor: errors.travelDates ? '#e53e3e' : '' }} />
              <ErrorMsg msg={errors.travelDates} />
            </div>
            <div className="form-group">
              <label className="form-label">Number of Days *</label>
              <input className="form-input" type="number" name="days" value={formData.days} onChange={handleInputChange} min="1" placeholder="e.g. 5" style={{ borderColor: errors.days ? '#e53e3e' : '' }} />
              <ErrorMsg msg={errors.days} />
            </div>
          </div>

          <div className="form-group" style={{ position: 'relative' }} ref={dropdownRef}>
            <label className="form-label">Travellers & Rooms *</label>
            <div
              className="form-input"
              style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', borderColor: errors.guests ? '#e53e3e' : '' }}
              onClick={() => setShowGuestDropdown(!showGuestDropdown)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 8, color: 'var(--text2)' }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
              {adults} adults · {children} children · {rooms} room{rooms > 1 ? 's' : ''}
            </div>
            <ErrorMsg msg={errors.guests} />

            {showGuestDropdown && (
              <div className="guest-dropdown" style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                background: '#fff', border: '1px solid var(--border)',
                borderRadius: 8, padding: 16, zIndex: 10,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }}>
                {/* Adults */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontWeight: 500, fontSize: 17 }}>Adults</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button type="button" onClick={() => setAdults(Math.max(1, adults - 1))} style={{ width: 32, height: 32, border: '1px solid var(--accent)', color: 'var(--accent)', background: 'transparent', borderRadius: 4, cursor: 'pointer' }}>-</button>
                    <span style={{ width: 20, textAlign: 'center', fontSize: 17 }}>{adults}</span>
                    <button type="button" onClick={() => setAdults(adults + 1)} style={{ width: 32, height: 32, border: '1px solid var(--accent)', color: 'var(--accent)', background: 'transparent', borderRadius: 4, cursor: 'pointer' }}>+</button>
                  </div>
                </div>

                {/* Children */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: children > 0 ? 8 : 16 }}>
                  <div style={{ fontWeight: 500, fontSize: 17 }}>Children</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button type="button" onClick={() => handleChildrenChange(-1)} style={{ width: 32, height: 32, border: '1px solid var(--accent)', color: 'var(--accent)', background: 'transparent', borderRadius: 4, cursor: children === 0 ? 'default' : 'pointer', opacity: children === 0 ? 0.3 : 1 }} disabled={children === 0}>-</button>
                    <span style={{ width: 20, textAlign: 'center', fontSize: 17 }}>{children}</span>
                    <button type="button" onClick={() => handleChildrenChange(1)} style={{ width: 32, height: 32, border: '1px solid var(--accent)', color: 'var(--accent)', background: 'transparent', borderRadius: 4, cursor: 'pointer' }}>+</button>
                  </div>
                </div>

                {/* Child Ages */}
                {children > 0 && (
                  <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {childAges.map((age, i) => (
                      <select
                        key={i}
                        className="form-input"
                        style={{ padding: '8px', fontSize: 16, borderColor: age === '' && errors.guests ? '#e53e3e' : 'var(--border)' }}
                        value={age}
                        onChange={(e) => handleAgeChange(i, e.target.value)}
                      >
                        <option value="">Age needed</option>
                        {[...Array(18)].map((_, idx) => (
                          <option key={idx} value={idx}>{idx} years old</option>
                        ))}
                      </select>
                    ))}
                    <div style={{ gridColumn: '1 / -1', fontSize: 14, color: 'var(--text2)', marginTop: 4, lineHeight: 1.4 }}>
                      To find a place to stay that fits your entire group along with correct prices, we need to know how old your children will be at check-out
                    </div>
                  </div>
                )}

                {/* Rooms */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontWeight: 500, fontSize: 17 }}>Rooms</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button type="button" onClick={() => setRooms(Math.max(1, rooms - 1))} style={{ width: 32, height: 32, border: '1px solid var(--accent)', color: 'var(--accent)', background: 'transparent', borderRadius: 4, cursor: 'pointer' }}>-</button>
                    <span style={{ width: 20, textAlign: 'center', fontSize: 17 }}>{rooms}</span>
                    <button type="button" onClick={() => setRooms(rooms + 1)} style={{ width: 32, height: 32, border: '1px solid var(--accent)', color: 'var(--accent)', background: 'transparent', borderRadius: 4, cursor: 'pointer' }}>+</button>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-primary"
                  style={{ width: '100%', padding: '10px 0', fontSize: 17, textAlign: 'center', justifyContent: 'center' }}
                  onClick={() => setShowGuestDropdown(false)}
                >
                  Done
                </button>
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Hotel Category *</label>
              <select className="form-input" name="hotelCategory" value={formData.hotelCategory} onChange={handleInputChange}>
                <option value="3 Star">3 Star</option>
                <option value="4 Star">4 Star</option>
                <option value="5 Star">5 Star</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Meal Plan *</label>
              <select className="form-input" name="mealPlan" value={formData.mealPlan} onChange={handleInputChange}>
                <option value="CPI (Breakfast only)">CPI (Breakfast only)</option>
                <option value="MAPI (Breakfast & Dinner)">MAPI (Breakfast & Dinner)</option>
                <option value="API (Breakfast, Lunch & Dinner)">API (Breakfast, Lunch & Dinner)</option>
              </select>
            </div>
          </div>

          <div className="modal__actions" style={{ marginTop: 24 }}>
            <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : (tour ? 'Send Booking Request' : 'Request Free Consultation')}
            </button>
            <button type="button" className="btn-ghost" onClick={onClose} disabled={isSubmitting}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── SIGN IN MODAL ─── */
export function SignInModal({ onClose, onSuccess }) {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSuccess('Signed in successfully! Welcome back.');
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-box--form" onClick={e => e.stopPropagation()}>
        <div className="modal-form-header">
          <div>
            <div className="modal__eyebrow" style={{ color: 'var(--accent)', marginBottom: 4 }}>Welcome Back</div>
            <h3 className="modal-form-title">Sign In</h3>
          </div>
          <button className="modal-close modal-close--dark" onClick={onClose}>×</button>
        </div>
        <form className="modal-form" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              className="form-input"
              type="email"
              placeholder="you@email.com"
              autoComplete="username"
              maxLength={100}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              className="form-input"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              minLength={8}
              maxLength={128}
              required
            />
          </div>
          <div className="modal__actions">
            <button type="submit" className="btn-primary" style={{ flex: 1 }}>Sign In</button>
          </div>
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button
              type="button"
              style={{ background: 'none', border: 'none', fontSize: 16, color: 'var(--text3)', cursor: 'pointer' }}
              onClick={() => onSuccess('Password reset link sent — check your inbox.')}
            >
              Forgot your password?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── SEARCH RESULTS MODAL ─── */
export function SearchModal({ searchData, onClose, onBook }) {
  const dest = searchData?.destination?.trim() ?? '';
  const results = dest
    ? tours.filter(t => t.name.toLowerCase().includes(dest.toLowerCase()) || t.region.toLowerCase().includes(dest.toLowerCase()))
    : tours;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-box--wide" onClick={e => e.stopPropagation()}>
        <div className="modal-form-header">
          <div>
            <div className="modal__eyebrow" style={{ color: 'var(--accent)', marginBottom: 4 }}>Search Results</div>
            <h3 className="modal-form-title">
              {dest ? `Tours matching "${dest}"` : 'All Available Tours'}
            </h3>
          </div>
          <button className="modal-close modal-close--dark" onClick={onClose}>×</button>
        </div>
        <div className="search-results">
          {results.length === 0 ? (
            <div className="search-empty">
              <div style={{ fontSize: 43, marginBottom: 12 }}>🗺️</div>
              <div style={{ fontSize: 19, color: 'var(--text2)', fontWeight: 300 }}>No tours found for that destination.</div>
              <button className="btn-primary" style={{ marginTop: 20 }} onClick={onClose}>Browse All Tours</button>
            </div>
          ) : (
            results.map(t => (
              <div key={t.id} className="search-result-item" onClick={() => onBook(t)}>
                <div className="search-result-img" style={{ backgroundImage: safeBgUrl(t.img) }} />
                <div className="search-result-info">
                  <div className="search-result-region">{t.region}</div>
                  <div className="search-result-name">{t.name}</div>
                  <div className="search-result-type">{t.type} · {t.days ? `${t.days} days` : 'Flexible'}</div>
                </div>

                <button className="btn-primary" style={{ padding: '10px 20px', fontSize: '14px' }} onClick={e => { e.stopPropagation(); onBook(t); }}>
                  Book
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
